import { createContext, useContext, useEffect, useState } from "react";

declare global {
  interface Window {
    tarteaucitron: {
      state: {
        [key in consentServices]: boolean;
      };
    };
  }
}

type consentServices = (typeof cookieServices)[number];

type consentEvents = {
  loaded: `${consentServices}_loadedScript`;
  denied: `${consentServices}_consentModeKo`;
  addedService: `${consentServices}_added`;
};

type cookieConsentEvent = Event & {
  detail?: {
    state: {
      [key in consentServices]?: boolean;
    };
    is_responded?: boolean;
    cookie_consent_event_name: consentEvents[keyof consentEvents];
  };
};

const cookieServices = [
  "chatbase",
  "facebookcustomerchat",
  "sentry",
  "ga_gtm",
  "openai"
] as const;

const getConsentType = (
  event_name?: consentEvents[keyof consentEvents]
): "response" | "add" => {
  if (!event_name) return "response";
  if (
    event_name.endsWith("_loadedScript") ||
    event_name.endsWith("_consentModeKo")
  ) {
    return "response";
  } else {
    return "add";
  }
};

const getConsentServiceKey = (
  event_name?: consentEvents[keyof consentEvents]
): consentServices | null => {
  if (!event_name) return null;

  const parts = event_name.split("_");

  // Handle services with underscores like "google_autocomplete"
  if (parts.length > 2) {
    return parts.slice(0, -1).join("_") as consentServices;
  }

  return parts[0] as consentServices;
};

type allowed = boolean;
const getConsentStatus = (
  event_name?: consentEvents[keyof consentEvents]
): allowed => {
  if (event_name?.endsWith("_loadedScript")) return true;
  return false;
};

const cookieServiceDefaultStatus: {
  [key in consentServices]: boolean;
} = Object.fromEntries(cookieServices.map((service) => [service, false])) as {
  [key in consentServices]: boolean;
};

export const cookieServicesEvents: { [key in consentServices]: consentEvents } =
  cookieServices.reduce((acc, serviceKey) => {
    acc[serviceKey] = {
      loaded: `${serviceKey}_loadedScript`,
      denied: `${serviceKey}_consentModeKo`,
      addedService: `${serviceKey}_added`
    };
    return acc;
  }, {} as { [key in consentServices]: consentEvents });

const cookieConsentState = (): {
  [key in consentServices]: boolean;
} => {
  if (!window) return cookieServiceDefaultStatus;
  const state = window.tarteaucitron?.state || cookieServiceDefaultStatus;
  return state;
};

const CookieConsentContext = createContext<{
  [key in consentServices]: boolean;
}>(cookieServiceDefaultStatus);

const CookieConsentProvider = ({ children }: { children: React.ReactNode }) => {
  const [consent, setConsent] = useState<{
    [key in consentServices]: boolean;
  }>(window.tarteaucitron?.state || cookieServiceDefaultStatus);

  useEffect(() => {
    const handleCookieConsent = (event: cookieConsentEvent) => {
      const eventName = event.detail?.cookie_consent_event_name;
      const type = getConsentType(eventName);
      const key = getConsentServiceKey(eventName);

      if (type === "add") {
        if (event.detail?.is_responded) setConsent(cookieConsentState());
      } else if (key) {
        const allow = getConsentStatus(eventName);
        setConsent((consent) => ({ ...consent, [key]: allow }));
      }
    };

    setConsent(cookieConsentState());

    Object.entries(cookieServicesEvents).forEach(([_, eventDictonary]) => {
      Object.values(eventDictonary).forEach((eventName) => {
        document.addEventListener(eventName, handleCookieConsent);
      });
    });

    return () => {
      Object.entries(cookieServicesEvents).forEach(([_, eventDictonary]) => {
        Object.values(eventDictonary).forEach((eventName) => {
          document.removeEventListener(eventName, handleCookieConsent);
        });
      });
    };
  }, []);

  return (
    <CookieConsentContext.Provider value={consent}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export default CookieConsentProvider;

export const useCookieConsent = () => {
  return useContext(CookieConsentContext);
};
